import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import './EmailSettings.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function EmailSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    smtp_host: 'smtp.hostinger.com',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: 'Skilled Safety Management Portal',
    admin_email: '',
    email_enabled: true
  });

  useEffect(() => {
    loadEmailSettings();
  }, []);

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const loadEmailSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/email-settings.php`, {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/admin');
          return;
        }
        throw new Error('Failed to load settings');
      }

      const data = await response.json();
      if (data.success && data.settings) {
        setFormData({
          smtp_host: data.settings.smtp_host || 'smtp.hostinger.com',
          smtp_port: data.settings.smtp_port || '587',
          smtp_username: data.settings.smtp_username || '',
          smtp_password: '',
          from_email: data.settings.from_email || '',
          from_name: data.settings.from_name || 'Skilled Safety Management Portal',
          admin_email: data.settings.admin_email || '',
          email_enabled: data.settings.email_enabled == 1
        });
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
      showMessage('error', 'Failed to load email settings');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/email-settings.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          email_enabled: formData.email_enabled ? 1 : 0
        })
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', '✓ Email settings saved successfully!');
        setTimeout(() => loadEmailSettings(), 1000);
      } else {
        showMessage('error', data.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      showMessage('error', 'An error occurred while saving settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_URL}/email-settings.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ test_email: true })
      });

      const data = await response.json();

      if (data.success) {
        showMessage('success', `✓ Test email sent to ${formData.admin_email}! Check your inbox.`);
      } else {
        showMessage('error', data.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      showMessage('error', 'An error occurred while sending test email');
    } finally {
      setTesting(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
  };

  return (
    <Layout>
      <div className="email-settings-page">
        {/* Page Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Email Notification Settings</h1>
            <p className="page-subtitle">Configure SMTP and email notifications for worker submissions</p>
          </div>
        </div>

        {/* Alert Message */}
        {message.text && (
          <div className={`modern-alert alert-${message.type}`}>
            <div className="alert-icon">
              {message.type === 'success' ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                  <path d="M9 12l2 2 4-4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                  <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2"/>
                  <circle cx="12" cy="16" r="0.5" fill="currentColor"/>
                </svg>
              )}
            </div>
            <span className="alert-text">{message.text}</span>
            <button className="alert-close" onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="email-settings-grid">
          {/* Left Column - Form */}
          <div className="settings-card">
            <div className="card-header">
              <h2 className="card-title">📧 SMTP Configuration</h2>
              <p className="card-subtitle">Email server settings</p>
            </div>

            <form onSubmit={handleSubmit} className="settings-form">
              {/* SMTP Server Settings */}
              <div className="form-section">
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="smtp_host">SMTP Host <span className="required">*</span></label>
                    <input
                      type="text"
                      id="smtp_host"
                      name="smtp_host"
                      value={formData.smtp_host}
                      onChange={handleChange}
                      placeholder="smtp.hostinger.com"
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="smtp_port">Port <span className="required">*</span></label>
                    <input
                      type="number"
                      id="smtp_port"
                      name="smtp_port"
                      value={formData.smtp_port}
                      onChange={handleChange}
                      placeholder="587"
                      min="1"
                      max="65535"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="smtp_username">SMTP Username <span className="required">*</span></label>
                    <input
                      type="email"
                      id="smtp_username"
                      name="smtp_username"
                      value={formData.smtp_username}
                      onChange={handleChange}
                      placeholder="noreply@yourdomain.com"
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="smtp_password">SMTP Password</label>
                    <input
                      type="password"
                      id="smtp_password"
                      name="smtp_password"
                      value={formData.smtp_password}
                      onChange={handleChange}
                      placeholder="Leave blank to keep current"
                    />
                  </div>
                </div>
              </div>

              {/* Email Settings */}
              <div className="form-section">
                <h3 className="section-title">Sender Information</h3>

                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="from_email">From Email <span className="required">*</span></label>
                    <input
                      type="email"
                      id="from_email"
                      name="from_email"
                      value={formData.from_email}
                      onChange={handleChange}
                      placeholder="noreply@yourdomain.com"
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="from_name">From Name <span className="required">*</span></label>
                    <input
                      type="text"
                      id="from_name"
                      name="from_name"
                      value={formData.from_name}
                      onChange={handleChange}
                      placeholder="Your Company Name"
                      required
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="admin_email">Admin Email (Recipient) <span className="required">*</span></label>
                  <input
                    type="email"
                    id="admin_email"
                    name="admin_email"
                    value={formData.admin_email}
                    onChange={handleChange}
                    placeholder="admin@yourdomain.com"
                    required
                  />
                  <small>Notifications will be sent to this email</small>
                </div>

                <div className="form-field" style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                  <label className="toggle-label">
                    <span className="toggle-text">Enable Email Notifications</span>
                    <input
                      type="checkbox"
                      name="email_enabled"
                      checked={formData.email_enabled}
                      onChange={handleChange}
                    />
                    <span className="toggle-switch"></span>
                  </label>
                  <small>Turn email notifications on or off for new worker submissions</small>
                </div>
              </div>

              {/* Form Actions */}
              <div className="form-actions">
                <button
                  type="submit"
                  className="modern-btn primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <span>💾</span>
                      <span>Save Settings</span>
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="modern-btn secondary"
                  onClick={handleTestEmail}
                  disabled={testing || !formData.admin_email}
                >
                  {testing ? (
                    <>
                      <span className="spinner"></span>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>📤</span>
                      <span>Send Test Email</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Right Column - Instructions */}
          <div className="instructions-card">
            <div className="card-header">
              <h2 className="card-title">
                <span>📖</span>
                <span>Setup Guide</span>
              </h2>
            </div>

            <div className="instructions-content">
                <div className="instruction-section">
                  <h4>🔧 Hostinger SMTP Setup</h4>
                  <ol>
                    <li>Login to <a href="https://hpanel.hostinger.com" target="_blank" rel="noopener noreferrer">Hostinger hPanel</a></li>
                    <li>Navigate to <strong>Emails</strong> section</li>
                    <li>Create or select an email account</li>
                    <li>Go to <strong>Configuration</strong> tab</li>
                    <li>Copy the SMTP settings shown</li>
                  </ol>
                </div>

                <div className="instruction-section">
                  <h4>📋 Common Settings</h4>
                  <div className="settings-list">
                    <div className="setting-item">
                      <strong>SMTP Host:</strong>
                      <code>smtp.hostinger.com</code>
                    </div>
                    <div className="setting-item">
                      <strong>SMTP Port:</strong>
                      <code>587 (TLS)</code> or <code>465 (SSL)</code>
                    </div>
                    <div className="setting-item">
                      <strong>Username:</strong>
                      <span>Full email address</span>
                    </div>
                    <div className="setting-item">
                      <strong>Password:</strong>
                      <span>Email account password</span>
                    </div>
                  </div>
                </div>

                <div className="instruction-section">
                  <h4>💡 Tips</h4>
                  <ul className="tips-list">
                    <li>Use a dedicated email like <code>noreply@yourdomain.com</code></li>
                    <li>Port 587 is recommended for better compatibility</li>
                    <li>Always test email after saving settings</li>
                    <li>Check spam folder if test email doesn't arrive</li>
                  </ul>
                </div>
              </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default EmailSettings;
